import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import TextInput from 'components/Common/Form/Input/TextInput'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import InputText from 'LuxKit/Typography/InputText'
import TextButton from 'LuxKit/Button/TextButton'

interface PromptModalProps {
  initialName?: string
  initialPromptId?: string
  mode?: 'add' | 'edit'
  initialNotes?: string
  onDelete?: () => void
}

export type PromptModalResult = {
  name: string
  promptId: string
  notes?: string
}

function PromptModal({ initialName = '', initialPromptId = '', initialNotes = '', mode = 'add', onDelete }: PromptModalProps) {
  const intl = useIntl()
  const modalElement = useModalElementContext<PromptModalResult | undefined>()
  const [name, setName] = useState(initialName)
  const [promptId, setPromptId] = useState(initialPromptId)
  const [notes, setNotes] = useState(initialNotes)
  const [nameError, setNameError] = useState<string | undefined>()
  const [promptIdError, setPromptIdError] = useState<string | undefined>()

  const isSaveDisabled = useMemo(
    () => name.trim() === '' || promptId.trim() === '',
    [name, promptId],
  )

  const openInOpenAI = useCallback(() => {
    const trimmedId = promptId.trim()
    if (!trimmedId) {
      return
    }
    const url = `https://platform.openai.com/chat/edit?prompt=${encodeURIComponent(trimmedId)}`
    window.open(url, '_blank', 'noreferrer')
  }, [promptId])

  const handleSave = useCallback(() => {
    const trimmedName = name.trim()
    const trimmedPromptId = promptId.trim()

    if (!trimmedName) {
      setNameError(intl.formatMessage({
        id: 'q8YBi4JLy97x0R7AKZzm',
        defaultMessage: 'Enter a name to display in the sidebar',
        description: 'Validation error when prompt name is empty in the staff prompt modal',
      }))
    }

    if (!trimmedPromptId) {
      setPromptIdError(intl.formatMessage({
        id: 'fHPGBbOsRU_FNVBlqPx6',
        defaultMessage: 'Enter a prompt ID',
        description: 'Validation error when prompt ID is empty in the staff prompt modal',
      }))
    }

    if (!trimmedName || !trimmedPromptId) {
      return
    }

    modalElement.resolve({
      name: trimmedName,
      promptId: trimmedPromptId,
      notes: notes.trim() || undefined,
    })
  }, [intl, modalElement, name, promptId, notes])

  const handleCancel = useCallback(() => {
    modalElement.resolve(undefined)
  }, [modalElement])

  const handleDelete = useCallback(() => {
    if (onDelete) {
      onDelete()
    }
    modalElement.resolve(undefined)
  }, [modalElement, onDelete])

  const isEditMode = mode === 'edit'

  const editPromptTitle = intl.formatMessage({
    id: 'I8zLvYFHBeDBbwVpC0e-',
    defaultMessage: 'Edit prompt',
    description: 'Modal title when editing an existing prompt in staff tools',
  })
  const addPromptTitle = intl.formatMessage({
    id: '3A512w412pyWWK7RcgAG',
    defaultMessage: 'Add prompt',
    description: 'Modal title when adding a new prompt in staff tools',
  })
  const saveLabel = intl.formatMessage({
    id: 'gR5GQT-7B8i-VofFvzeE',
    defaultMessage: 'Save',
    description: 'Primary action button label to save an edited prompt',
  })
  const addLabel = intl.formatMessage({
    id: 'bRcREMQO_NEp51bvxFnC',
    defaultMessage: 'Add',
    description: 'Primary action button label to add a new prompt in the trip planner AI chat',
  })
  const deleteLabel = intl.formatMessage({
    id: 'B_oFHGZc89GNmIVAc_J4',
    defaultMessage: 'Delete',
    description: 'Secondary action button label to delete a prompt in the trip planner AI chat',
  })
  const cancelLabel = intl.formatMessage({
    id: '1m3e_gYTJMe4yAVLhNA1',
    defaultMessage: 'Cancel',
    description: 'Secondary action button label to cancel prompt modal',
  })
  const nameLabel = intl.formatMessage({
    id: 'tBBOnoihZMlLY4pKN2y5',
    defaultMessage: 'Name',
    description: 'Input label for the prompt name field in staff tools',
  })
  const namePlaceholder = intl.formatMessage({
    id: 'vj8Vm0NEZIS-1Q-idTtj',
    defaultMessage: 'e.g. V2, Explore, Personal',
    description: 'Placeholder text for the prompt name input in staff tools',
  })
  const promptIdLabel = intl.formatMessage({
    id: 'H8dDczpUQ1tE6rYcZ40i',
    defaultMessage: 'Prompt ID',
    description: 'Input label for the prompt ID field in staff tools',
  })
  const promptIdPlaceholder = intl.formatMessage({
    id: 'kcbwMlfPT1wtDCtva-79',
    defaultMessage: 'pmpt_...',
    description: 'Placeholder text for the prompt ID input showing expected format in staff tools',
  })
  const notesLabel = intl.formatMessage({
    id: 'aijuKG8bIyn2SAHaPy0D',
    defaultMessage: 'Notes (optional)',
    description: 'Input label for the optional notes field in staff tools',
  })
  const notesPlaceholder = intl.formatMessage({
    id: '0Fa7xJ9bgs2yYrfkcs6j',
    defaultMessage: 'Add context for this prompt',
    description: 'Placeholder text for the notes input in staff tools',
  })
  const openInOpenAILabel = intl.formatMessage({
    id: '1irJSTBR9Pqt2RBygJDI',
    defaultMessage: 'Open in OpenAI',
    description: 'Button label to open the prompt in OpenAI platform',
  })

  return (
    <Modal
      title={isEditMode ? editPromptTitle : addPromptTitle}
      primaryActionText={isEditMode ? saveLabel : addLabel}
      isPrimaryActionDisabled={isSaveDisabled}
      secondaryActionText={isEditMode && onDelete ? deleteLabel : cancelLabel}
      onPrimaryActionClick={handleSave}
      onSecondaryActionClick={isEditMode && onDelete ? handleDelete : handleCancel}
      onClose={handleCancel}
      height="auto"
    >
      <Group direction="vertical" gap={12}>
        <Group direction="vertical" gap={4}>
          <InputText variant="label">
            {nameLabel}
          </InputText>
          <TextInput
            value={name}
            onChange={(e) => {
              setName(e.currentTarget.value)
              if (nameError) {
                setNameError(undefined)
              }
            }}
            manualError
            invalidErrorMessage={nameError}
            placeholder={namePlaceholder}
            autoFocus
          />
        </Group>
        <Group direction="vertical" gap={4}>
          <InputText variant="label">
            {promptIdLabel}
          </InputText>
          <TextInput
            value={promptId}
            onChange={(e) => {
              setPromptId(e.currentTarget.value)
              if (promptIdError) {
                setPromptIdError(undefined)
              }
            }}
            manualError
            invalidErrorMessage={promptIdError}
            placeholder={promptIdPlaceholder}
          />
        </Group>
        <Group direction="vertical" gap={4}>
          <InputText variant="label">
            {notesLabel}
          </InputText>
          <TextInput
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            placeholder={notesPlaceholder}
          />
        </Group>
        <Group direction="horizontal" gap={8}>
          <TextButton
            kind="secondary"
            size="small"
            onClick={openInOpenAI}
            disabled={promptId.trim() === ''}
          >
            {openInOpenAILabel}
          </TextButton>
        </Group>
      </Group>
    </Modal>
  )
}

export default PromptModal
